import type * as Immutable from 'immutable';
import { createMigration } from './utils';

const NEW_Y_POSITIONS_BY_ASPECT_RATIO: Record<string, number> = {
    landscape: 970,
    vertical: 1340,
    square: 950,
    fourfive: 1220,
};

function updateTransformation(
    map: Immutable.Map<string, unknown>,
    path: (string | number)[],
    newYAnchor: number,
    newYPosition?: number,
) {
    map.setIn([...path, 'transformation', 'anchor', 'y'], newYAnchor);
    if (newYPosition !== undefined) {
        map.setIn([...path, 'transformation', 'position', 'y'], newYPosition);
    }
}

export const migrationTo9_225 = createMigration({
    migrateVideo(video) {
        return video.withMutations((map) => {
            map.get('slides').forEach((scene: Immutable.Map<string, unknown>, i: number) => {
                map.setIn(['slides', i], this.migrateScene(scene));
            });
        });
    },
    migrateScene(scene) {
        const sceneDesignId = scene.get('sceneDesign') as string | undefined;
        const sceneCategory = scene.get('sceneCategory') as string | undefined;

        if (
            !sceneDesignId ||
            !sceneCategory ||
            !sceneDesignId.includes('caption_') ||
            sceneCategory !== 'caption'
        ) {
            return scene;
        }

        const aspectRatio = scene.get('aspectRatio') as string;
        const newYPosition = NEW_Y_POSITIONS_BY_ASPECT_RATIO[aspectRatio];
        const newYAnchor = 0.5;

        return scene.withMutations((map) => {
            map.get('children', []).forEach((_child: unknown, cIndex: number) => {
                const path = ['children', cIndex];
                const asset = map.getIn(path) as Immutable.Map<string, unknown> | undefined;
                if (!asset) {
                    return;
                }
                const childClassType = asset.get('classType');
                if (childClassType !== 'CanvasTextAsset') {
                    return;
                }
                // we don't need to migrate this scene if the y anchor is already 0.5
                const currentYAnchor = asset.getIn(['transformation', 'anchor', 'y']);
                if (currentYAnchor === newYAnchor) {
                    return;
                }
                updateTransformation(map, path, newYAnchor, newYPosition);
            });

            const textPositionPresets = map.get('textPositionPresets') as
                | Immutable.Map<string, Immutable.List<Immutable.Map<string, unknown>>>
                | undefined;
            if (textPositionPresets && textPositionPresets.size > 0) {
                const firstPresetKey = textPositionPresets.keySeq().first() as string;
                const presetAssets = textPositionPresets.get(firstPresetKey);
                presetAssets?.forEach((asset: Immutable.Map<string, unknown>, pIndex: number) => {
                    if (
                        asset.get('classType') !== 'CanvasTextAsset' &&
                        asset.get('classType') !== 'CanvasFancyCaptionAsset'
                    ) {
                        return;
                    }
                    const currentYAnchor = asset.getIn(['transformation', 'anchor', 'y']);
                    if (currentYAnchor === newYAnchor) {
                        return;
                    }
                    const path = ['textPositionPresets', firstPresetKey, pIndex];
                    updateTransformation(map, path, newYAnchor, newYPosition);
                });
            }
        });
    },
});
